import type { Fraction } from '@mimir/common/util/fractionType';
import type { AudioCodec, BufferRange, PacketProvider, PacketProviderInfo, Packet, VideoCodec, PacketProviderStreamInfoVideo, PacketProviderStreamInfoAudio, VideoPacket } from './demuxAndDecodeFrameProvider';
import type { EncodedAudioData, EncodedData, EncodedVideoData } from '@mimir/common/mpegts/demuxerTypes';
import { addFractions, approximateFractionValue, compareFractions, divideFractionBy, divideFractions, frac, invertFraction, maxFractions, minFractions, multiplyFractionBy, roundFraction, simplifyFraction, subtractFractions } from '@mimir/common/util/integerMath';
import type { Logger } from '@mimir/common/types/logger';
import { PrefixedLogger } from '@mimir/common/log/prefixedLogger';
import { abortify } from '@mimir/common/util/abortify';
import type { PacketCache, PacketIterator } from './packetCache';
import { createPacketCache } from './packetCache';
import { isAbortError } from '@mimir/common/util/abortControl';
import { binarySearch } from '@mimir/common/util/binarySearch';
import { PromiseUtil } from '@mimir/common/async/async';
import type { PreloadInfo } from '@mimir/cutter/frameProvider/frameProvider';
import { invariant } from '@mimir/common/util/invariant';
import type { StructuredLogger } from '@mimir/primitive/structuredLogger';

export interface SegmentInfo {
  start: Fraction,
  end: Fraction,
  id: string,
}

export interface SegmentProvider {
  /**
   *  Return the TS segment at the given position. If no position is given it
   *  should return the first segment. May return is the provided position is
   *  outside of the media duration.
   */
  getSegmentInfoAt(position: Fraction): Promise<SegmentInfo | null>;

  getSegment(id: string): Promise<EncodedData>;

  getDuration(): Promise<Fraction>;
}

export async function createSegmentedPacketProvider(
  outerLogger: Logger,
  segmentProvider: SegmentProvider,
): Promise<PacketProvider> {
  const logger = new PrefixedLogger(outerLogger, '[SegmentPacketProvider]');
  return await SegmentedPacketProvider.create(logger, segmentProvider);
}

// ----

function videoCodecFromEncodedVideoData(encodedVideoData: EncodedVideoData): VideoCodec {
  if (!encodedVideoData.codec.startsWith('avc1')) {
    throw new Error(`Unsupported video codec: ${encodedVideoData.codec}`);
  }

  return 'h264';
}

function calculateFrameRateFromEncodedVideoData(encodedVideoData: EncodedVideoData): Fraction {
  if (encodedVideoData.frames.length <= 1) {
    throw new Error('Must have multiple video packets in frame in order to be able to determine frame rate');
  }

  const first = encodedVideoData.frames[0];
  const last = encodedVideoData.frames[encodedVideoData.frames.length - 1];

  const deltaDts : Fraction = { numerator: last.dts - first.dts, denominator: encodedVideoData.timescale };

  const frameCountMinus1AsFraction : Fraction = {
    numerator: encodedVideoData.frames.length - 1,
    denominator: 1
  };

  const frameDuration = divideFractions(deltaDts, frameCountMinus1AsFraction);

  const frameRate = invertFraction(frameDuration);
  return encodedVideoData.exactFrameDuration ? frameRate : repairFrameRate(frameRate);
}

function findMaxGopSizeInEncodedVideoData(encodedVideoData: EncodedVideoData): number {
  invariant(encodedVideoData.frames.length > 0, 'Empty segment');
  let lastKeyFrame = 0;
  let gopSize = 0;
  for (let i = 0; i < encodedVideoData.frames.length; i++) {
    const frame = encodedVideoData.frames[i];
    gopSize = Math.max(gopSize, i - lastKeyFrame);
    if (frame.keyframe) {
      lastKeyFrame = i;
    }
  }
  return Math.max(gopSize, encodedVideoData.frames.length - lastKeyFrame);
}

function repairFrameRate(frameRate: Fraction): Fraction {
  const fps = approximateFractionValue(frameRate);
  return simplifyFraction({ numerator: Math.round(fps * 1001), denominator: 1001 });
}

function audioCodecFromEncodedAudioData(encodedAudioData: EncodedAudioData): AudioCodec {
  switch (encodedAudioData.codec) {
    case 'mp4a.40.2': return 'aac';
    default: throw new Error(`Unknown audio codec: ${encodedAudioData.codec}`);
  }
}

interface FetchedSegment {
  info: SegmentInfo,
  encodedData: EncodedData,
}

const ZERO : Fraction = { numerator: 0, denominator: 1 };

interface ExtendedPacketProviderStreamInfoAudio extends PacketProviderStreamInfoAudio {
  firstAudioPacketPts: Fraction
}

type ExtendedPacketProviderStreamInfo =
  PacketProviderStreamInfoVideo |
  ExtendedPacketProviderStreamInfoAudio;

interface SegmentAnalysisResult {
  startOffset: Fraction,
  hasVideo: boolean,
  frameRate: Fraction,
  audioStreamCount: number,
  streams: ExtendedPacketProviderStreamInfo[],
}

interface ExtendedPacketProviderInfo extends PacketProviderInfo {
  streams: ExtendedPacketProviderStreamInfo[],
}

function debug(logger: StructuredLogger, message: string, info: any = { }) {
  logger.log({ level: 'debug', prefix: ['SegmentPacketProvider'], message, ...info });
}

class SegmentedPacketProvider implements PacketProvider {
  private flushed : boolean = false;
  private packetIterator : PacketIterator | null = null;
  private cache : PacketCache;
  private bufferRanges : { start: Fraction, end: Fraction }[] = [];
  private packetProviderInfo : ExtendedPacketProviderInfo;
  private startOffset : Fraction;
  private hasVideo: boolean;
  private audioStreamCount: number;
  private segmentTracker : SegmentTracker;
  private playbackDirection : 'forward' | 'backward' = 'forward';
  private frameDuration: Fraction;

  private constructor(
    private logger: StructuredLogger,
    segmentProvider: SegmentProvider,
    firstSegment: FetchedSegment,
    duration: Fraction,
  ) {
    this.cache = createPacketCache();

    const analysis = SegmentedPacketProvider.analyzeFirstSegment(firstSegment.encodedData);

    this.packetProviderInfo = { streams: analysis.streams, duration, };
    this.startOffset = analysis.startOffset;
    this.hasVideo = analysis.hasVideo;
    this.audioStreamCount = analysis.audioStreamCount;
    this.frameDuration = invertFraction(analysis.frameRate);

    this.segmentTracker =
      new SegmentTracker(
        segmentProvider,
        (fetchedSegment: FetchedSegment, firstSegment: boolean): PopulateInfo => {
          const analysis = SegmentedPacketProvider.analyzeFirstSegment(fetchedSegment.encodedData);
          SegmentedPacketProvider.updatePacketProviderInfo(this.logger, this.packetProviderInfo, analysis);
          return this.populateCacheFromSegment(fetchedSegment, firstSegment);
        }
      );

    this.segmentTracker.addEntry(firstSegment, true);
  }

  public static async create(logger: StructuredLogger, segmentProvider: SegmentProvider): Promise<SegmentedPacketProvider> {
    // TODO: Dynamic duration update from live items.
    const duration = await segmentProvider.getDuration();
    const firstSegmentInfo = await segmentProvider.getSegmentInfoAt(ZERO);
    if (firstSegmentInfo === null) {
      throw new Error('Unable to fetch first segment');
    }
    const firstSegment = await segmentProvider.getSegment(firstSegmentInfo.id);
    return new SegmentedPacketProvider(logger, segmentProvider, { encodedData: firstSegment, info: firstSegmentInfo }, duration);
  }

  async seek(logger: StructuredLogger, position: Fraction, abortSignal: AbortSignal): Promise<void> {
    this.flushed = false;
    debug(logger, 'seek()', { position });
    this.packetIterator = this.cache.seek(logger, position);
    if (this.packetIterator !== null) {
      return;
    }

    debug(logger, 'seek(): cache miss, will populate packet cache from segment');

    let result = await abortify(abortSignal, () => this.segmentTracker.populateCacheInProximityOf(logger, position));

    debug(logger, 'seek(): populate result', { result });

    if (result.type !== 'ok') {
      debug(logger, 'seek(): failed. populate did not provide any more data.');
      return;
    }

    this.packetIterator = this.cache.seek(logger, position);
    if (this.packetIterator !== null) {
      debug(logger, 'seek(): succeeded to create iterator after first populate attempt');
      return;
    }

    result = await abortify(abortSignal, () => this.segmentTracker.populateCacheInProximityOf(logger, position));

    debug(logger, 'seek(): populate result', { result });

    if (result.type !== 'ok') {
      debug(logger, 'seek(): failed. second populate did not provide any more data.');
      return;
    }

    this.packetIterator = this.cache.seek(logger, position);
    if (this.packetIterator !== null) {
      debug(logger, 'seek(): ok. got content after second populate');
    } else {
      debug(logger, 'seek(): fail. unable to create iterator after fetching both segments in proximity');
    }
  }

  async poll(logger: StructuredLogger, abortSignal: AbortSignal): Promise<Packet | null> {
    debug(logger, 'poll()');
    if (this.packetIterator === null) {
      debug(logger, 'poll(): packetIterator is null');
      return null;
    }

    const packet = this.packetIterator.packet();

    try {
      await this.iterateToNextPacket(logger, abortSignal);
    } catch (error) {
      if (isAbortError(error)) {
        this.packetIterator = null;
      }
      throw error;
    }

    return packet;
  }

  async preload(logger: StructuredLogger, startAt: Fraction, preloadInfo: PreloadInfo): Promise<void> {
    this.playbackDirection = preloadInfo.direction;
    await this.preloadInternal(logger, startAt, this.playbackDirection);
  }

  bufferedRanges(): BufferRange[] {
    return this.bufferRanges.map((b) => ({ kind: 'demuxed', ...b }));
  }

  info(): PacketProviderInfo {
    return this.packetProviderInfo;
  }

  private async preloadInternal(logger: StructuredLogger, startAt: Fraction, direction: 'forward' | 'backward'): Promise<void> {
    await this.segmentTracker.preload(logger, startAt, direction, 5);
  }

  private static updatePacketProviderStreamInfoVideo(
    _logger: StructuredLogger,
    toUpdate: PacketProviderStreamInfoVideo,
    newInfo: PacketProviderStreamInfoVideo,
  ) {
    toUpdate.gopSize = Math.max(toUpdate.gopSize, newInfo.gopSize);
  }

  private static updatePacketProviderStreamInfoAudio(
    _logger: StructuredLogger,
    _toUpdate: PacketProviderStreamInfoAudio,
    _newInfo: PacketProviderStreamInfoAudio,
  ) {
    // Nothing to update so far
  }

  private static updatePacketProviderInfo(
    logger: StructuredLogger,
    toUpdate: PacketProviderInfo,
    newInfo: SegmentAnalysisResult,
  ) {
    if (toUpdate.streams.length != newInfo.streams.length) {
      logger.log({ level: 'warn', message: 'Number of streams in segments is inconsistent', currentStreamCount: toUpdate.streams.length, segmentStreamCount: newInfo.streams.length });
    }

    for (let i = 0; (i < newInfo.streams.length) && (i < toUpdate.streams.length); i++) {
      const streamToUpdate = toUpdate.streams[i];
      const newStreamInfo = newInfo.streams[i];
      if (streamToUpdate.type === 'video') {
        if (newStreamInfo.type === 'video') {
          SegmentedPacketProvider.updatePacketProviderStreamInfoVideo(logger, streamToUpdate, newStreamInfo);
        } else {
          logger.log({ level: 'warn', message: 'Stream changed from being video to something else', streamIndex: i });
        }
      }
      if (streamToUpdate.type === 'audio') {
        if (newStreamInfo.type === 'audio') {
          SegmentedPacketProvider.updatePacketProviderStreamInfoAudio(logger, streamToUpdate, newStreamInfo);
        } else {
          logger.log({ level: 'warn', message: 'Stream changed from being audio to something else', streamIndex: i });
        }
      }
    }
  }

  private static analyzeFirstSegment(encodedData: EncodedData): SegmentAnalysisResult {
    const streams: ExtendedPacketProviderStreamInfo[] = [];

    let startOffset : Fraction = { numerator: 0, denominator: 1 };
    let frameRate : Fraction = { numerator: 25, denominator: 1 };

    const hasVideo = (encodedData.encodedVideoData !== undefined);
    if (encodedData.encodedVideoData !== undefined) {
      frameRate = calculateFrameRateFromEncodedVideoData(encodedData.encodedVideoData);
      const gopSize = findMaxGopSizeInEncodedVideoData(encodedData.encodedVideoData);
      startOffset = { numerator: encodedData.encodedVideoData.frames[0].pts, denominator: encodedData.encodedVideoData.timescale };
      streams.push({
        type: 'video',
        codec: videoCodecFromEncodedVideoData(encodedData.encodedVideoData),
        gopSize,
        frameRate,
      });
    }

    const audioStreamCount = encodedData.encodedAudioData.length;
    for (const encodedAudioData of encodedData.encodedAudioData) {
      const codec = audioCodecFromEncodedAudioData(encodedAudioData);
      if (!hasVideo) {
        // If there is no video, we adjust the start offset to the first actual frame.
        // If the audio is AAC, that means skipping over the very first packet that is used
        // as initialization
        const firstFrameIndex = codec === 'aac' ? 1 : 0;
        startOffset = { numerator: encodedAudioData.frames[firstFrameIndex].pts, denominator: encodedAudioData.timescale };
      }
      const sampleRate = encodedAudioData.sampleRateInHz;

      streams.push({
        type: 'audio',
        codec,
        sampleRate,
        channelCount: encodedAudioData.numberOfChannels,
        firstAudioPacketPts: frac(encodedAudioData.frames[0].pts, encodedAudioData.timescale),
      });
    }

    return { startOffset, hasVideo, streams, audioStreamCount, frameRate };
  }

  private populateCacheFromSegment(segment: FetchedSegment, firstSegment: boolean): PopulateInfo {
    const { low, high } = this.populateCacheFromEncodedData(segment.encodedData, firstSegment);

    const overlapLow = maxFractions(low, segment.info.start);
    const overlapHigh = minFractions(high, segment.info.end);

    const ptsRangeSize = subtractFractions(high, low);
    const overlap = subtractFractions(overlapHigh, overlapLow);
    const overlapRatio = divideFractions(overlap, ptsRangeSize);

    if (compareFractions(overlapRatio, { numerator: 1, denominator: 2 }) < 0) {
      throw new Error(`SegmentProvider validation failed. Segment with logical time interval of [${approximateFractionValue(segment.info.start)}, ${approximateFractionValue(segment.info.end)}> overlaps less than 50% with the actual PTS range of [${approximateFractionValue(low)}, ${approximateFractionValue(high)}>.`);
    }

    return {
      segmentRange: {
        start: segment.info.start,
        end: segment.info.end,
      },
      ptsRange: {
        start: low,
        end: high,
      }
    };
  }

  private populateCacheFromEncodedData(encodedData: EncodedData, firstSegment: boolean): { low: Fraction, high: Fraction } {
    if ((encodedData.encodedVideoData !== undefined) !== this.hasVideo) {
      throw new Error('Presence of video stream changed mid-stream');
    }
    if (encodedData.encodedAudioData.length !== this.audioStreamCount) {
      throw new Error(`Number of audio streams changed mid stream: ${encodedData.encodedAudioData.length} != ${this.audioStreamCount}`);
    }

    let low : Fraction = { numerator: 1, denominator: 0 }; // +"infinity"
    let high: Fraction = { numerator: -1, denominator: 0 }; // -"infinity"

    const packets : Packet[] = [];

    let streamIndex = 0;
    if (encodedData.encodedVideoData !== undefined) {
      // We keep packets sorted by DTS in the packet cache to ensure that
      // we can feed them to the decoder in cache order. However, with files
      // containing B-frames, DTS values can be shifted relative to PTS values—
      // for example, the first DTS might be -2 while the first PTS is 0.
      // To handle this, we adjust (shift) the DTS values to maintain their
      // correct relative order compared to PTS, while preserving the
      // original timing offset.
      const dtsDelta = encodedData.encodedVideoData.frames[0].dts - encodedData.encodedVideoData.frames[0].pts;

      let firstPacketInSegment = true;
      for (const encodedFrame of encodedData.encodedVideoData.frames) {
        const packet : VideoPacket = {
          type: 'video',
          streamIndex,
          codec: videoCodecFromEncodedVideoData(encodedData.encodedVideoData),
          codecString: encodedData.encodedVideoData.codec,
          keyFrame: encodedFrame.keyframe,
          progressive: encodedData.encodedVideoData.progressive,
          pts: subtractFractions({ numerator: encodedFrame.pts, denominator: encodedData.encodedVideoData.timescale }, this.startOffset),
          dts: subtractFractions({ numerator: encodedFrame.dts - dtsDelta, denominator: encodedData.encodedVideoData.timescale }, this.startOffset),
          duration: addFractions(this.frameDuration, encodedData.encodedVideoData.epsilon),
          payload: encodedFrame.data,
          description: encodedData.encodedVideoData.description,
          firstPacket: firstSegment && firstPacketInSegment,
        };

        const end = addFractions(packet.dts, packet.duration);

        low = minFractions(packet.dts, low);
        high = maxFractions(end, high);

        firstPacketInSegment = false;

        packets.push(packet);
      }
      streamIndex++;
    }
    for (const encodedAudioData of encodedData.encodedAudioData) {
      const codec = audioCodecFromEncodedAudioData(encodedAudioData);
      const sampleRate = encodedAudioData.sampleRateInHz;
      let adjustInfo : AudioTimestampAdjustmentInfo | null = null;

      const streamInfo = this.packetProviderInfo.streams[streamIndex];

      // Since it is not possible to accurately express the timestamp of
      // 44.1khz AAC in MPEG-TS we need to snap the packet PTS values to
      // exact sample offsets in order to avoid gaps in the packet cache.
      // While you'd think that it would be enough to snap to the closest
      // sample offset it seems that certain encoders (ffmpeg for instance)
      // misses with up to 2 audio samples when writing the MPEG-TS timestamps.
      if (streamInfo.type === 'audio') {
        adjustInfo = {
          startOffset: this.startOffset,
          firstAudioPacketPts: streamInfo.firstAudioPacketPts,
          gridSizeInSamples: 2,
          sampleRate,
        };
      }

      let firstPacketInSegment = true;
      for (let i = 0; i < encodedAudioData.frames.length; i++) {
        const encodedFrame = encodedAudioData.frames[i];
        const packet : Packet = {
          type: 'audio',
          streamIndex,
          codec,
          pts: adjustAudioTimestamp(frac(encodedFrame.pts, encodedAudioData.timescale), adjustInfo),
          dts: adjustAudioTimestamp(frac(encodedFrame.dts, encodedAudioData.timescale), adjustInfo),
          duration: adjustAudioDuration(frac(encodedFrame.duration, encodedAudioData.timescale), adjustInfo, encodedAudioData.epsilon),
          payload: encodedFrame.data,
          sampleRate,
          firstPacket: firstSegment && firstPacketInSegment,
        };

        const end = addFractions(packet.dts, packet.duration);

        low = minFractions(packet.dts, low);
        high = maxFractions(end, high);

        firstPacketInSegment = false;

        packets.push(packet);
      }
      streamIndex++;
    }

    packets.sort((a, b) => compareFractions(a.dts, b.dts));
    this.cache.push(this.logger, packets);

    if (this.packetIterator) {
      this.packetIterator.refresh();
    }

    appendToRanges({ start: low, end: high }, this.bufferRanges);

    return { low, high };
  }

  private async iterateToNextPacket(logger: StructuredLogger, abortSignal: AbortSignal): Promise<void> {
    if (this.packetIterator === null) {
      return;
    }
    const nextIterator = this.packetIterator.next(logger, this.flushed);
    if (nextIterator !== null) {
      this.packetIterator = nextIterator;
      return;
    }

    debug(logger, 'poll(): iterator returned null.', { flushed: this.flushed });

    if (this.flushed) {
      debug(logger, 'poll(): got null segment after flush. reached end.');
      this.packetIterator = null;
      return;
    }

    const currentPacket = this.packetIterator.packet();
    const currentPacketEnd = addFractions(currentPacket.dts, currentPacket.duration);

    const result = await abortify(abortSignal, () => this.segmentTracker.populateCacheInProximityOf(logger, currentPacketEnd));

    debug(logger, 'populate cache result', { result });

    if (result.type === 'end') {
      debug(logger, 'poll(): got null segment. flushing now.');
      this.flushed = true;
      this.packetIterator = this.packetIterator.next(logger, this.flushed);
      return;
    }

    if (result.type === 'skip') {
      debug(logger, 'poll(): no additional data to fetch a given position. ending iterator');
      this.packetIterator = null;
      return;
    }

    this.packetIterator = this.packetIterator.next(logger, this.flushed);

    if (!this.flushed && !this.packetIterator) {
      throw new Error('Populated cached cache without having reached the end but iterator could not progress. This could indicate a GAP in the packets');
    }
  }
}

function appendToRanges(
  range: { start: Fraction, end: Fraction },
  ranges: { start: Fraction, end: Fraction }[]
) {
  ranges.push(range);
  const deltas : { time: Fraction, delta: number }[] = [];
  for (const r of ranges) {
    deltas.push({ time: r.start, delta: 1 });
    deltas.push({ time: r.end, delta: -1 });
  }
  deltas.sort((a, b) => compareFractions(a.time, b.time));

  const outputRanges : { start: Fraction, end: Fraction }[] = [];
  let currentRangeStart : Fraction | null = null;
  let overlapCount = 0;
  for (const delta of deltas) {
    overlapCount += delta.delta;
    if (currentRangeStart === null) {
      if (overlapCount > 0) {
        currentRangeStart = delta.time;
      }
    } else if (overlapCount === 0) {
      outputRanges.push({ start: currentRangeStart, end: delta.time });
      currentRangeStart = null;
    }
  }

  ranges.splice(0);
  ranges.push(...outputRanges);
}

interface PopulateInfo {
  segmentRange: { start: Fraction, end: Fraction },
  ptsRange: { start: Fraction, end: Fraction },
}

interface PopulateResultOk {
  type: 'ok',
  populateInfo: PopulateInfo,
}

interface PopulateResultSkipOrEnd {
  type: 'skip' | 'end',
}

type PopulateResult = PopulateResultOk | PopulateResultSkipOrEnd;

interface SegmentTrackerEntryCached extends SegmentInfo {
  type: 'cached',
}

interface SegmentTrackerEntryPending extends SegmentInfo {
  type: 'pending',
  promise: Promise<PopulateResult>,
}

type SegmentTrackerEntry = SegmentTrackerEntryCached | SegmentTrackerEntryPending;

/**
 *  This class is reponsible for keeping track of which segments have been
 *  fetched or are currently being fetched and allows fetch requests to
 *  piggy back on existing fetches.
 */
class SegmentTracker {
  entries: SegmentTrackerEntry[] = [];

  constructor(
    private segmentProvider: SegmentProvider,
    private populateCacheFromSegment: (fetchedSegment: FetchedSegment, firstSegment: boolean) => PopulateInfo,
  ) { }

  async populateCacheAt(logger: StructuredLogger, position: Fraction): Promise<PopulateResult> {
    logger.log({ level: 'debug', message: 'populateCacheAt()', position });
    const entry = await this.getOrCreatePopulateEntry(logger, position);

    if (entry === null) {
      logger.log({ level: 'debug', message: 'populateCacheAt: end' });
      return { type: 'end' };
    }

    logger.log({ level: 'debug', message: 'populateCacheAt: has entry', type: entry.type, start: entry.start, end: entry.end });

    switch (entry.type) {
      case 'pending': return entry.promise;
      case 'cached': return { type: 'skip' };
    }
  }

  async populateCacheInProximityOf(logger: StructuredLogger, position: Fraction): Promise<PopulateResult> {
    logger.log({ level: 'debug', message: 'populateCacheInProximityOf()', position });
    const index =
      binarySearch<{ end: Fraction }>(
        this.entries,
        { end: position },
        (a, b) => compareFractions(a.end, b.end),
      );

    if (index === -1) {
      logger.log({ level: 'debug', message: 'populateCacheInProximityOf: no existing segment tracked', position });
      return await this.populateCacheAt(logger, position);
    }

    const entry = this.entries[index];
    if (compareFractions(entry.start, position) > 0) {
      logger.log({ level: 'debug', message: 'populateCacheInProximityOf: no existing segment tracked', position });
      return await this.populateCacheAt(logger, position);
    }

    if (entry.type === 'pending') {
      logger.log({ level: 'debug', message: 'populateCacheInProximityOf: piggy-backing on pending tracked segment entry', position, start: entry.start, end: entry.end });
      return entry.promise;
    }

    // The segment at the exact position was already fetched, let's try the
    // neighbor segment.

    const midPoint = divideFractionBy(addFractions(entry.start, entry.end), 2);

    const minSegmentSize = { numerator: 1, denominator: 1000 };

    const segmentTime =
      (compareFractions(position, midPoint) < 0) ?
        subtractFractions(entry.start, minSegmentSize) :
        addFractions(entry.end, minSegmentSize);

    logger.log({ level: 'debug', message: 'populateCacheInProximityOf: trying neighbour segment at', segmentTime });

    return await this.populateCacheAt(logger, segmentTime);
  }

  async preload(logger: StructuredLogger, startAt: Fraction, direction: 'forward' | 'backward', segmentCount: number) {
    let position = startAt;

    const minSegmentDuration = { numerator: 1, denominator: 1000 };
    const promises = new PromiseUtil();
    for (let i = 0; i < segmentCount; i++) {
      const entry = await this.getOrCreatePopulateEntry(logger, position);
      if (entry === null) {
        // Reached end
        break;
      }
      if (entry.type === 'pending') {
        promises.push(entry.promise);
      }

      if (direction === 'forward') {
        position = entry.end;
      } else {
        position = subtractFractions(entry.start, minSegmentDuration);
      }
    }

    await promises.waitForAll();
  }

  private async getOrCreatePopulateEntry(logger: StructuredLogger, position: Fraction): Promise<SegmentTrackerEntry | null> {
    let entry = this.getEntryAndIndex(position)?.entry;
    if (entry) {
      logger.log({ level: 'debug', message: 'getOrCreatePopulateEntry(): got entry', entry });
      return entry;
    }

    const info = await this.segmentProvider.getSegmentInfoAt(position);

    // We also want to check after returning from getting the segment
    // info since another thread could have started the fetch in the mean
    // time.
    entry = this.getEntryAndIndex(position)?.entry;

    if (entry) {
      logger.log({ level: 'debug', message: 'getOrCreatePopulateEntry(): got entry', entry });
      return entry;
    }

    if (info === null) {
      logger.log({ level: 'debug', message: 'getOrCreatePopulateEntry(): segment provider returned null' });
      return null;
    }

    const promise : Promise<PopulateResult> = this.downloadSegmentAndPopulate(info)
      .then((populateInfo) => {
        this.addOrUpdateEntry({ type: 'cached', ...info });
        const result : PopulateResult = { type: 'ok', populateInfo };
        return result;
      })
      .catch((error) => {
        // TODO: Maybe store the error so that we don't retry to the error
        // segment?
        this.removeEntry(info.start);
        throw error;
      });

    const newEntry : SegmentTrackerEntry = { type: 'pending', promise, ...info };

    logger.log({ level: 'debug', message: 'getOrCreatePopulateEntry(): creating new entry', newEntry });

    this.addOrUpdateEntry(newEntry);

    return newEntry;
  }


  private async downloadSegmentAndPopulate(info: SegmentInfo): Promise<PopulateInfo> {
    const encodedData = await this.segmentProvider.getSegment(info.id);

    const fetchedSegment = { encodedData, info };

    return this.populateCacheFromSegment(fetchedSegment, false);
  }

  private addOrUpdateEntry(entry: SegmentTrackerEntry) {
    const index =
      binarySearch<{ start: Fraction }>(
        this.entries,
        entry,
        (a, b) => compareFractions(a.start, b.start),
      );

    if (index === -1) {
      this.entries.push(entry);
      return;
    }

    const existingEntry = this.entries[index];
    if (compareFractions(existingEntry.start, entry.start) === 0) {
      // update
      this.entries[index] = entry;
      return;
    }

    this.entries.splice(index, 0, entry);
  }

  private removeEntry(position: Fraction) {
    const index = this.getEntryAndIndex(position)?.index ?? null;
    if (index !== null) {
      this.entries.splice(index, 1);
    }
  }

  private getEntryAndIndex(position: Fraction): { entry: SegmentTrackerEntry, index: number } | null {
    let index =
      binarySearch<{ end: Fraction }>(
        this.entries,
        { end: position },
        (a, b) => compareFractions(a.end, b.end)
      );

    if (index === -1) {
      return null;
    }
    let entry = this.entries[index];
    if (compareFractions(entry.end, position) === 0) {
      index++;
      if (index === this.entries.length) {
        return null;
      }
      entry = this.entries[index];
    }
    if (compareFractions(entry.start, position) > 0) {
      return null;
    }

    return { entry, index };
  }

  public addEntry(fetchedSegment: FetchedSegment, firstSegment: boolean) {
    this.entries.push({ type: 'cached', ...fetchedSegment.info });
    this.populateCacheFromSegment(fetchedSegment, firstSegment);
  }

}

interface AudioTimestampAdjustmentInfo {
  startOffset: Fraction,
  firstAudioPacketPts: Fraction,
  sampleRate: number,
  gridSizeInSamples: number,
}

function adjustAudioTimestamp(ts: Fraction, info: AudioTimestampAdjustmentInfo | null): Fraction {
  if (info === null) {
    return { ...ts };
  }

  const relativeFirstAudioPacketPts = subtractFractions(info.firstAudioPacketPts, info.startOffset);
  const relativeTs = subtractFractions(ts, info.startOffset);

  const gridOffset = relativeFirstAudioPacketPts;

  const grid = frac(info.gridSizeInSamples, info.sampleRate);

  const normalized = subtractFractions(relativeTs, gridOffset);
  const packetIndex = roundFraction(divideFractions(normalized, grid));

  return addFractions(multiplyFractionBy(grid, packetIndex), gridOffset);
}

function adjustAudioDuration(
  duration: Fraction,
  info: AudioTimestampAdjustmentInfo | null,
  epsilon: Fraction,
): Fraction {
  if (info === null) {
    return addFractions(duration, epsilon);
  }
  const gridSize = frac(info.gridSizeInSamples, info.sampleRate);
  const durationInGridCount = roundFraction(divideFractions(duration, gridSize));

  return multiplyFractionBy(gridSize, durationInGridCount);
}

